/**
 * Welche Module dieser Mandant führt — für die Bausteine über der Anwendung.
 *
 * ## Warum es das gibt
 *
 * Der Zuschnitt eines Mandanten steht in `PROFILE` (`entwurf/navigation.ts`)
 * und wird in `App.tsx` aufgelöst. Alles, was **unterhalb** von `App` liegt,
 * bekommt ihn als Prop — Spalten, Abschnitte, Routen.
 *
 * Der Drawer liegt aber **darüber**: `main.tsx` hängt ihn über `Anlegeweg`
 * um die ganze Anwendung, damit ein Formular jede Route überdauert. React
 * reicht Kontext nach unten weiter, nicht nach oben — der Drawer konnte den
 * Zuschnitt deshalb nicht kennen.
 *
 * Das ist am 27.08.2026 aufgefallen: HOWOGE führt keine Kritikalität, die
 * Spalte war überall ausgeblendet — und im Applikationsformular stand sie
 * weiter, weil das Formular von Modulen nichts wusste. Der Kunde hat es
 * gemeldet.
 *
 * ## Wie es funktioniert
 *
 * Dasselbe Muster wie `Seitenaktion.tsx`: der Rahmen liegt über beidem,
 * `App` **meldet** seinen Zuschnitt an, und wer darüber liegt, liest ihn.
 * Kein zweiter Ort, an dem der Zuschnitt entschieden wird — `PROFILE` bleibt
 * die einzige Quelle, hier fließt er nur eine Ebene nach oben.
 *
 * Die Vorbelegung ist **leer**, und das ist Absicht: ein unbekanntes Modul
 * blendet nichts aus (`!== false`). Solange `App` nichts gemeldet hat, sieht
 * man also alles — nie weniger, als einem zusteht.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { Modul } from '../entwurf/navigation';

type Module = Partial<Record<Modul, boolean>>;

interface Api {
  module: Module;
  melden: (module: Module) => void;
}

const Zusammenhang = createContext<Api | null>(null);

export function Modulrahmen({ children }: { children: ReactNode }) {
  const [module, setzeModule] = useState<Module>({});
  const melden = useCallback((neu: Module) => setzeModule(neu), []);
  const wert = useMemo(() => ({ module, melden }), [module, melden]);
  return <Zusammenhang.Provider value={wert}>{children}</Zusammenhang.Provider>;
}

/** Der Zuschnitt des Mandanten. Ohne Rahmen: leer, also nichts ausgeblendet. */
export function useModule(): Module {
  return useContext(Zusammenhang)?.module ?? {};
}

/**
 * Meldet den Zuschnitt an — genau eine Stelle ruft das: `App`.
 *
 * Verglichen wird über die Werte und nicht über die Kennung des Objekts:
 * `profil.module` ist bei jedem Zeichnen dasselbe Objekt, ein neu gebautes
 * wäre es nicht — und dann meldete `App` bei jedem Zeichnen neu und der
 * Rahmen zeichnete alles darunter noch einmal.
 */
export function useModuleMelden(module: Module): void {
  const rahmen = useContext(Zusammenhang);
  const melden = rahmen?.melden;
  const fingerabdruck = JSON.stringify(module);
  useEffect(() => {
    melden?.(JSON.parse(fingerabdruck) as Module);
  }, [melden, fingerabdruck]);
}
